import React, { useRef, useState, useCallback } from 'react'
import {
  metersToDisplayDistance, metersToElev, mpsToSpeed,
  distanceUnit, elevUnit, speedUnit
} from '../../../../../lib/trackStats'

// Nominal SVG coordinate space (stretched to the container via preserveAspectRatio="none").
const NW = 1000

// Slope-grade color scale (abs %), per the design handoff.
export const gradeColor = (g) => {
  const a = Math.abs(g)
  if (a < 4) return '#86A94A'
  if (a < 10) return '#C9A23F'
  if (a < 18) return '#C67238'
  return '#B94038'
}

const MIN_GAP = 3 // minimum kept index span between trim handles

/**
 * Elevation profile chart used in both the read-only view (mode="view", slope-graded
 * stroke + hover crosshair) and the Edit > Trim block (mode="trim", adds draggable
 * handles, shaded trimmed regions, and a hover tooltip).
 *
 * Props:
 *   points        derived per-point array from buildTrackPoints()
 *   units         'imperial' | 'metric'
 *   mode          'view' | 'trim'
 *   color         track color (trim stroke + hover dot)
 *   height        rendered px height (104 view / 120 trim)
 *   trim          [startIdx, endIdx] (trim mode)
 *   onTrimChange  (startIdx, endIdx) => void
 *   onTrimCommit  () => void  (pointer up; parent handles Free snap-back)
 *   onHover       (index|null) => void
 */
const TrackChart = (props) => {
  const {
    points = [], units = 'imperial', mode = 'view', color = '#FF0000',
    height = 104, trim, onTrimChange, onTrimCommit, onHover,
    // Lines have no time/speed data — hide those tooltip fields when false.
    showTimeSpeed = true
  } = props

  const boxRef = useRef(null)
  const dragRef = useRef(null) // 'start' | 'end' while dragging
  const [hoverIdx, setHoverIdx] = useState(null)

  const n = points.length
  const NH = mode === 'trim' ? 120 : 104

  if (n < 2) {
    return <div ref={boxRef} style={{ width: '100%', height }} />
  }

  const totalM = points[n - 1].distM || 1
  const elevsDisp = points.map(p => metersToElev(p.elevM, units))
  const minE = Math.min(...elevsDisp)
  const maxE = Math.max(...elevsDisp)
  const padLo = units === 'imperial' ? 12 : 4
  const padHi = units === 'imperial' ? 14 : 5
  const eLo = minE - padLo
  const eHi = maxE + padHi
  const eSpan = eHi - eLo || 1

  // Clamp indices — trim/hover state can briefly outlive a shorter points array
  // (e.g. right after switching tracks, before the parent resets it).
  const clampIdx = (i) => (i < 0 ? 0 : i >= n ? n - 1 : i)
  const xAt = (i) => (points[clampIdx(i)].distM / totalM) * NW
  const yAt = (i) => NH - ((elevsDisp[clampIdx(i)] - eLo) / eSpan) * NH

  // Build line + area path strings.
  let line = ''
  for (let i = 0; i < n; i++) line += `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(i).toFixed(1)}`
  const area = `${line}L${NW},${NH}L0,${NH}Z`

  // Fraction (0..1) -> nearest point index by cumulative distance.
  const idxAtFrac = (f) => {
    const target = f * totalM
    let best = 0, bestD = Infinity
    for (let i = 0; i < n; i++) {
      const d = Math.abs(points[i].distM - target)
      if (d < bestD) { bestD = d; best = i }
    }
    return best
  }
  const fracFromEvent = (e) => {
    const rect = boxRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
  }

  // Single pointer-move handler; branches at call time on the drag ref (which a
  // render-time ternary can't see, since starting a drag only mutates the ref).
  const onMove = (e) => {
    const i = idxAtFrac(fracFromEvent(e))
    if (dragRef.current && trim) {
      let [s, en] = trim
      if (dragRef.current === 'start') s = Math.max(0, Math.min(i, en - MIN_GAP))
      else en = Math.min(n - 1, Math.max(i, s + MIN_GAP))
      onTrimChange && onTrimChange(s, en)
    }
    setHoverIdx(i)
    onHover && onHover(i)
  }

  const handleLeave = () => {
    if (dragRef.current) return // keep hover while dragging (pointer captured)
    setHoverIdx(null)
    onHover && onHover(null)
  }

  const startDrag = (which) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = which
    // Capture on the container so move/up keep firing here during the drag.
    try { boxRef.current.setPointerCapture(e.pointerId) } catch (_) {}
  }
  const endDrag = (e) => {
    if (!dragRef.current) return
    dragRef.current = null
    try { boxRef.current.releasePointerCapture(e.pointerId) } catch (_) {}
    onTrimCommit && onTrimCommit()
  }

  const isTrim = mode === 'trim'
  const gradeOn = mode === 'view'
  const stroke = gradeOn ? 'url(#tc-grade)' : color
  const areaFill = isTrim ? rgba(color, 0.18) : 'url(#tc-efade)'
  const cross = hoverIdx != null ? xAt(hoverIdx) : null
  const ts = clampIdx(trim ? trim[0] : 0)
  const te = clampIdx(trim ? trim[1] : n - 1)

  // Hover readout values (used by the view tooltip).
  const hp = hoverIdx != null ? points[clampIdx(hoverIdx)] : null

  return (
    <div
      ref={boxRef}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerLeave={handleLeave}
      style={{ position: 'relative', width: '100%', height, cursor: 'crosshair', touchAction: 'none' }}
    >
      <svg
        width="100%" height="100%" viewBox={`0 0 ${NW} ${NH}`}
        preserveAspectRatio="none" style={{ overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id="tc-efade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(158,204,59,0.30)" />
            <stop offset="100%" stopColor="rgba(158,204,59,0.03)" />
          </linearGradient>
          {gradeOn && (
            <linearGradient id="tc-grade" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={NW} y2="0">
              {points.filter((_, i) => i % 2 === 0).map((p, k) => (
                <stop key={k} offset={(p.distM / totalM)} stopColor={gradeColor(p.gradePct)} />
              ))}
            </linearGradient>
          )}
        </defs>

        {isTrim && [30, 60, 90].map(y => (
          <line key={y} x1="0" y1={y} x2={NW} y2={y} stroke="rgba(255,255,255,0.08)" />
        ))}

        <path d={area} fill={areaFill} />
        <path d={line} fill="none" stroke={stroke} strokeWidth={isTrim ? 2 : 2.5}
          strokeLinejoin="round" vectorEffect="non-scaling-stroke" />

        {isTrim && (
          <>
            <rect x="0" y="0" width={xAt(ts)} height={NH} fill="rgba(0,0,0,0.55)" />
            <rect x={xAt(te)} y="0" width={NW - xAt(te)} height={NH} fill="rgba(0,0,0,0.55)" />
          </>
        )}

        {cross != null && (
          <line x1={cross} y1="0" x2={cross} y2={NH} stroke="rgba(255,255,255,0.55)" vectorEffect="non-scaling-stroke" />
        )}
      </svg>

      {/* Hover dot as an HTML element so it stays a perfect circle — an SVG <circle>
          would be stretched into an ellipse by preserveAspectRatio="none". */}
      {cross != null && (
        <div style={{
          position: 'absolute',
          left: `${(hp.distM / totalM) * 100}%`,
          top: `${(yAt(hoverIdx) / NH) * 100}%`,
          width: 9, height: 9, borderRadius: '50%',
          background: color, border: '1.5px solid #fff',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none', zIndex: 4
        }} />
      )}

      {isTrim && trim && (
        <>
          <Handle pct={(points[ts].distM / totalM) * 100} onDown={startDrag('start')} />
          <Handle pct={(points[te].distM / totalM) * 100} onDown={startDrag('end')} />
        </>
      )}

      {/* Hover readout tooltip shows on the Track Details (view) chart only —
          not while editing/trimming. */}
      {!isTrim && hp && (
        <div style={tooltipStyle((hoverIdx / (n - 1)) * 100)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 500, color: '#fff' }}>
            <span>{metersToDisplayDistance(hp.distM, units).toFixed(2)} {distanceUnit(units)}</span>
            {showTimeSpeed && <span style={{ color: '#959595', fontWeight: 300 }}>{fmtClock(hp.timeS)}</span>}
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 11, fontWeight: 300, color: '#c1c0c0' }}>
            <span>{Math.round(metersToElev(hp.elevM, units))} {elevUnit(units)}</span>
            {showTimeSpeed && <span>{mpsToSpeed(hp.mps, units).toFixed(1)} {speedUnit(units)}</span>}
            <span>{hp.gradePct.toFixed(0)}% grade</span>
          </div>
        </div>
      )}
    </div>
  )
}

const Handle = ({ pct, onDown }) => (
  <div
    onPointerDown={onDown}
    style={{
      position: 'absolute', top: -8, bottom: -2, width: 18, marginLeft: -9,
      left: `${pct}%`, cursor: 'ew-resize', touchAction: 'none', zIndex: 3
    }}
  >
    <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: '#f2f2f2' }} />
    <div style={{ position: 'absolute', left: 3, top: -4, width: 12, height: 20, borderRadius: 6, border: '1px solid rgba(0,0,0,0.5)', background: '#f2f2f2' }} />
  </div>
)

const tooltipStyle = (leftPct) => ({
  position: 'absolute', top: -52, left: `clamp(4px, calc(${leftPct}% - 94px), calc(100% - 192px))`,
  width: 188, background: 'rgba(0,0,0,0.88)', border: '1px solid #3a3a3a', borderRadius: 5,
  padding: '7px 10px', zIndex: 5, pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 2
})

const fmtClock = (timeS) => {
  if (!timeS) return ''
  try {
    return new Date(timeS * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch (e) { return '' }
}

// hex (#rrggbb) -> rgba() with alpha
function rgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m) return `rgba(158,204,59,${a})`
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`
}

export default TrackChart
